"use client";

import {
  lazy,
  Suspense,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
} from "react";
import { ErrorBoundary } from "@/components/error/error-boundary";

interface LazyComponentOptions {
  loading: ReactNode;
  ssr?: boolean;
  failure?: { label: string; fallback: ReactNode };
}

const subscribeToNothing = () => () => {};
const isRenderingOnClient = () => true;
const isRenderingOnServer = () => false;

export function lazyComponent<Props extends object>(
  load: () => Promise<ComponentType<Props>>,
  { loading, ssr = true, failure }: LazyComponentOptions,
): ComponentType<Props> {
  const Loaded = lazy(() =>
    load().then((component) => ({ default: component })),
  );

  return function LazyComponent(props: Props) {
    const isClient = useSyncExternalStore(
      subscribeToNothing,
      isRenderingOnClient,
      isRenderingOnServer,
    );
    if (!ssr && !isClient) return loading;

    const content = (
      <Suspense fallback={loading}>
        <Loaded {...props} />
      </Suspense>
    );
    if (!failure) return content;
    return (
      <ErrorBoundary label={failure.label} fallback={failure.fallback}>
        {content}
      </ErrorBoundary>
    );
  };
}
